import { FC } from 'react';

import { EnquiryType } from '@shared/api/enquiry/enquiry.interface';
import { Icon } from '@shared/components/icons';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Link } from 'react-router-dom';

import { FuneralHomeEnquiryForm } from './FuneralHomeEnquiryForm';

//* `?help=` 값. 뒤로 가기로 목록에 돌아올 수 있게 URL에 둔다
export const ENQUIRY_HELP_PARAM = 'help';

export enum EnquiryQuickHelpKey {
  FuneralHome = 'funeral-home',
  PastBugo = 'past-bugo',
  AccountDisplay = 'account-display',
}

export const isEnquiryQuickHelpKey = (
  value: string | null,
): value is EnquiryQuickHelpKey =>
  value !== null && (Object.values(EnquiryQuickHelpKey) as string[]).includes(value);

const ITEMS: { key: EnquiryQuickHelpKey; label: string }[] = [
  { key: EnquiryQuickHelpKey.FuneralHome, label: '장례식장 정보를 추가·수정하고 싶어요' },
  { key: EnquiryQuickHelpKey.PastBugo, label: '지난 부고를 다시 볼 수 있나요?' },
  {
    key: EnquiryQuickHelpKey.AccountDisplay,
    label: '상주 계좌를 전체·여러 개 보여주고 싶어요',
  },
];

export const QUICK_HELP_TITLE: Record<EnquiryQuickHelpKey, string> = {
  [EnquiryQuickHelpKey.FuneralHome]: '장례식장 정보 추가·수정',
  [EnquiryQuickHelpKey.PastBugo]: '지난 부고 다시 보기',
  [EnquiryQuickHelpKey.AccountDisplay]: '상주 계좌 전체·여러 개 표시',
};

const QUICK_HELP_DESCRIPTION: Record<EnquiryQuickHelpKey, string> = {
  [EnquiryQuickHelpKey.FuneralHome]: '요청 내용을 확인한 뒤 반영하고 답변드려요.',
  [EnquiryQuickHelpKey.PastBugo]: '지난 부고의 확인 가능 기간을 안내해드려요.',
  [EnquiryQuickHelpKey.AccountDisplay]: '설정 순서와 표시 방식을 확인해보세요.',
};

/**
 * 문의하기 탭 맨 위의 "자주 묻는 문의" 3개.
 * 지도사 문의에서 반복되는 주제를 양식·안내로 바로 끝낸다 (docs/enquiry-quick-help-spec.md).
 */
export const EnquiryQuickHelpList: FC<{
  onSelect: (key: EnquiryQuickHelpKey) => void;
}> = ({ onSelect }) => {
  const { ChevronRight } = Icon();
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-bold text-gray-700">자주 묻는 문의</h2>
      <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {ITEMS.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
              onClick={() => onSelect(item.key)}
            >
              <span>{item.label}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

//* 2. 지난 부고 — 접수 없이 안내만. 발인 5일 후 agenda.deletePrivateData가 비공개 처리한다
const PastBugoNotice: FC = () => (
  <section className="overflow-hidden rounded-xl border border-gray-200 bg-white text-sm leading-6">
    <div className="space-y-3 p-4">
      <h3 className="break-keep text-base font-bold text-gray-900">
        지난 부고는 다시 볼 수 없어요
      </h3>
      <p className="break-keep text-gray-600">
        개인정보 보호를 위해 발인 5일 후 부고 데이터를 파기 처리하고 있어 다시 볼 수
        없습니다.
      </p>
    </div>
    <div className="space-y-1 border-t border-gray-100 bg-gray-50 p-4">
      <p className="font-medium text-gray-800">발인 후 5일 이내</p>
      <p className="break-keep text-gray-600">
        필요한 내용이 있다면 이 기간 안에 미리 저장해 주세요.
      </p>
    </div>
  </section>
);

//* 3. 계좌 전체·복수 표시 — 이미 있는 기능(부고수정 → 계좌설정 탭)의 위치 안내
const AccountDisplayNotice: FC = () => (
  <div className="space-y-4 text-sm leading-6">
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="break-keep text-base font-bold text-gray-900">
        상주 계좌는 부고 수정에서 직접 설정할 수 있어요
      </h3>
      <ol className="mt-4 space-y-4">
        {[
          {
            title: '해당 부고 선택',
            description: <>부고 관리에서 설정할 부고를 선택해주세요.</>,
          },
          {
            title: '계좌설정',
            description: (
              <>
                <b className="font-medium">부고수정</b> → 계좌설정 탭으로 이동해주세요.
              </>
            ),
          },
          {
            title: '부의금 계좌 표시',
            description: <>상주별 계좌 설정에서 표시 방식을 골라주세요.</>,
          },
        ].map((step, index) => (
          <li key={step.title} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600"
            >
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="font-bold text-gray-800">{step.title}</p>
              <p className="break-keep text-gray-600">{step.description}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <h3 className="border-b border-gray-100 bg-gray-50 px-4 py-3 font-medium text-gray-700">
        어떤 방식으로 보여줄까요?
      </h3>
      <dl className="divide-y divide-gray-100 px-4">
        <div className="flex items-start gap-4 py-3">
          <dt className="w-16 shrink-0 font-bold text-gray-800">전체</dt>
          <dd className="break-keep text-gray-600">등록된 모든 계좌를 보여줍니다.</dd>
        </div>
        <div className="flex items-start gap-4 py-3">
          <dt className="w-16 shrink-0 font-bold text-gray-800">직접 선택</dt>
          <dd className="break-keep text-gray-600">함께 보여줄 계좌를 고릅니다.</dd>
        </div>
      </dl>
    </section>
    <p className="break-keep px-1 text-xs leading-5 text-gray-500">
      다른 상주의 계좌가 먼저 등록되어 있어야 선택할 수 있어요.
    </p>
  </div>
);

interface ViewProps {
  helpKey: EnquiryQuickHelpKey;
  type: EnquiryType;
  onBack: () => void;
  onSuccess: () => void;
}

/** 항목을 고른 뒤의 화면 — 상단 뒤로 가기 + 양식 또는 안내 */
export const EnquiryQuickHelpView: FC<ViewProps> = ({
  helpKey,
  type,
  onBack,
  onSuccess,
}) => {
  const { ChevronRight } = Icon();
  const { isAdmin, isBugoAgencyWorker } = useAuth();
  const canGoManage =
    helpKey === EnquiryQuickHelpKey.AccountDisplay && (isAdmin() || isBugoAgencyWorker());
  return (
    <div className="space-y-5">
      <header className="space-y-3 border-b border-gray-200 pb-5">
        <button
          type="button"
          className="-ml-2 inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 text-sm text-gray-500 hover:bg-gray-100 focus-visible:ring-1 focus-visible:ring-gray-400"
          onClick={onBack}
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          자주 묻는 문의
        </button>
        <div className="space-y-2">
          <h2 className="break-keep text-xl font-bold text-gray-900">
            {QUICK_HELP_TITLE[helpKey]}
          </h2>
          <p className="break-keep text-sm leading-6 text-gray-500">
            {QUICK_HELP_DESCRIPTION[helpKey]}
          </p>
        </div>
      </header>
      {helpKey === EnquiryQuickHelpKey.FuneralHome && (
        <FuneralHomeEnquiryForm type={type} onSuccess={onSuccess} />
      )}
      {helpKey === EnquiryQuickHelpKey.PastBugo && <PastBugoNotice />}
      {helpKey === EnquiryQuickHelpKey.AccountDisplay && <AccountDisplayNotice />}
      {helpKey !== EnquiryQuickHelpKey.FuneralHome && (
        <div className="space-y-2 pt-1">
          {canGoManage && (
            <Link
              to="/fevent/manage"
              className="flex min-h-[48px] w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-3 text-sm font-bold text-white hover:bg-gray-800 focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
            >
              부고 관리로 이동
            </Link>
          )}
          <button
            type="button"
            onClick={onBack}
            className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 focus-visible:ring-1 focus-visible:ring-gray-400"
          >
            다른 문의 보기
          </button>
        </div>
      )}
    </div>
  );
};
